import omit from 'lodash.omit';
import { Observable } from 'rxjs';
import { fullUserAgent, isIosApp } from 'utils/platform';

import { HasToken } from '../model/AuthenticationInfo';

export class ErrorResponse {
  response: any = {};
  isOffline: boolean;

  constructor(readonly error: any) {
    this.isOffline = this.error.status === 0;
  }
}

export class AjaxClientThatThrows {
  private ajaxOptions: any;

  constructor(readonly authInfo?: HasToken) {
    const headers =
      authInfo && authInfo.token
        ? { WithlocalsAuthenticator: authInfo.token }
        : undefined;
    const userAgentHeaders = isIosApp()
      ? { 'User-Agent': fullUserAgent() }
      : {};
    this.ajaxOptions = {
      withCredentials: true,
      crossDomain: true,
      headers: {
        ...headers,
        ...userAgentHeaders,
        'Content-Type': 'application/json',
      },
    };
  }

  get = (url: string, body: {} = {}) => {
    return Observable.ajax({
      ...this.ajaxOptions,
      url,
      body,
      responseType: 'json',
      method: 'GET',
    })
      .do((r) => console.debug('AjaxClient.get', r))
      .retryWhen((errors) => {
        // rety offline errors 5 times once every second
        return errors
          .scan((errorCount, err) => {
            if (err.status > 0) {
              throw err;
            }
            // incrementally slow down retry
            return errorCount + 1;
          }, 0)
          .delay(1000)
          .take(5);
      });
  };

  post = (url: string, body: {} = {}, headers: {} = {}) => {
    const observable = Observable.ajax({
      ...this.ajaxOptions,
      url,
      body,
      responseType: 'json',
      method: 'POST',
      headers: {
        ...this.ajaxOptions?.headers,
        ...headers,
      },
    });

    return observable.do((r) => console.log('AjaxClient.post', r));
  };

  del = (url: string, body: {} = {}) => {
    return Observable.ajax({
      ...this.ajaxOptions,
      url,
      body,
      responseType: 'json',
      method: 'DELETE',
    }).do((r) => console.debug('AjaxClient.del', r));
  };

  put = (url: string, body: {} = {}) => {
    return Observable.ajax({
      ...this.ajaxOptions,
      url,
      body,
      responseType: 'json',
      method: 'PUT',
    }).do((r) => console.debug('AjaxClient.put', r));
  };
}

const sensitiveBodyKeys = ['password'];

export class AjaxClient {
  private client: AjaxClientThatThrows;

  constructor(readonly authInfo?: HasToken) {
    this.client = new AjaxClientThatThrows(authInfo);
  }

  omitUserSensitiveFields = (body: {}): any => {
    return omit(body, sensitiveBodyKeys);
  };

  get = (url: string, body: {} = {}) => {
    return this.client.get(url, body).catch((error) => {
      console.error('Error when GET: ', {
        url,
        body: this.omitUserSensitiveFields(body),
        response: error.response,
      });
      return Observable.of(new ErrorResponse(error));
    });
  };

  post = (url: string, body: {} = {}, headers: {} = {}) => {
    return this.client.post(url, body, headers).catch((error) => {
      console.error('Error when POST: ', {
        url,
        body: this.omitUserSensitiveFields(body),
        response: error.response,
      });
      return Observable.of(new ErrorResponse(error));
    });
  };

  del = (url: string, body: {} = {}) => {
    return this.client.del(url, body).catch((error) => {
      console.error('Error when DELETE: ', {
        url,
        body: this.omitUserSensitiveFields(body),
        response: error.response,
      });
      return Observable.of(new ErrorResponse(error));
    });
  };

  put = (url: string, body: {} = {}) => {
    return this.client.put(url, body).catch((error) => {
      console.error('Error when PUT: ', {
        url,
        body: this.omitUserSensitiveFields(body),
        response: error.response,
      });
      return Observable.of(new ErrorResponse(error));
    });
  };
}
